/**
 * PURPOSE: Consumir el panel administrativo de Comunicados (Hayllis notifications) desde Winscore.
 * RESPONSIBILITIES: Listar/detallar comunicados (borradores y publicados), crear/editar borradores,
 *   publicar, ocultar y subir el adjunto opcional — deliberadamente distinto de
 *   auditNotificationService.js/AuditNotificationPanel (auditoría interna, no relacionada).
 * COLLABORATORS: componentes en src/admin/HayllisCommunications/, backend
 *   /api/hayllis-notifications (FTR-0005 Phase 2/3, rol 1).
 * ROUTE: winscore/src/services/hayllisNotificationsService.js
 */
import httpService from "./httpService.js";

const BASE_PATH = "/api/hayllis-notifications";

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const toArray = (value) => (Array.isArray(value) ? value : []);

export const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.msg || error?.message || fallback;

export const listHayllisCommunications = async ({ limit = 20, beforeId, status } = {}) => {
  const response = await httpService.get(`${BASE_PATH}/notifications`, {
    params: {
      limit,
      ...(beforeId ? { beforeId } : {}),
      ...(status ? { status } : {}),
    },
  });
  const payload = unwrap(response);
  return {
    items: toArray(payload.items),
    nextCursor: payload.nextCursor ?? null,
    hasMore: Boolean(payload.hasMore),
  };
};

export const getHayllisCommunicationDetail = async (id) => {
  const response = await httpService.get(`${BASE_PATH}/notifications/${id}`);
  return unwrap(response)?.notification;
};

export const createHayllisCommunicationDraft = async ({ title, body, attachment }) => {
  const response = await httpService.post(`${BASE_PATH}/notifications`, {
    title,
    body,
    attachment: attachment ?? null,
  });
  return unwrap(response)?.notification;
};

// `patch` is sent as-is: only include the keys the caller actually wants to change. The backend
// distinguishes an omitted `attachment` key (leave unchanged) from an explicit `attachment: null`
// (remove) — HayllisCommunicationForm always sends all three fields on save, which is a valid
// (if not minimal) patch under that same contract.
export const updateHayllisCommunicationDraft = async (id, patch) => {
  const response = await httpService.patch(`${BASE_PATH}/notifications/${id}`, patch);
  return unwrap(response)?.notification;
};

export const publishHayllisCommunication = async (id) => {
  const response = await httpService.post(`${BASE_PATH}/notifications/${id}/publish`);
  return unwrap(response)?.notification;
};

export const hideHayllisCommunication = async (id) => {
  const response = await httpService.post(`${BASE_PATH}/notifications/${id}/hide`);
  return unwrap(response)?.notification;
};

// Returns the trusted {url, mime, name, size} envelope the backend produced — never let the
// caller construct or edit a URL by hand (FTR-0005 §15: no arbitrary attachment URL input).
export const uploadHayllisCommunicationAttachment = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await httpService.post(`${BASE_PATH}/attachments`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return unwrap(response);
};
